import { useState } from 'react';
import type { OutreachCandidate } from '../../types/taOutreach';
import { SelectBox } from './BulkApprove';

/** Whether a card can be ticked for "Approve N": a draft waiting for review that Approve would take. */
export const canBulkApprove = (c: OutreachCandidate, drafting: Set<string>) =>
  c.status === 'drafted' && Boolean(c.draft?.body?.trim()) && !c.sentAsStale && !c.draftOutdated && !drafting.has(c.id);

/**
 * The ticks for "Approve N" (Sofia, Oct 28 2026). Only cards Approve would take count, so a tick on
 * a card that was approved, redrafted or flagged meanwhile is simply ignored. `boxFor(c)` is the
 * card's checkbox, or nothing when it cannot be ticked.
 *
 * **Select all stays on** (Sofia, Nov 2 2026: "Select all missed the last card"): it was a snapshot
 * of the cards at the click, so a card whose draft came in afterwards was not ticked. Now, while it
 * is on, every card Approve would take counts — also ones that become approvable later — until a
 * card is unticked (then the rest stay ticked as a plain set) or Select all is unticked.
 * `stillDrafting` = cards that cannot be ticked yet because their draft is being (or about to be,
 * `draftsComing`) written.
 */
export function useBulkSelection(queue: OutreachCandidate[], drafting: Set<string>, draftsComing = true) {
  const [picked, setPicked] = useState<Set<string> | 'all'>(new Set());
  const approvable = queue.filter((c) => canBulkApprove(c, drafting));
  const ids = new Set(approvable.map((c) => c.id));
  const allOn = picked === 'all';
  const selected = allOn ? ids : picked;
  const stillDrafting = queue.filter((c) => !ids.has(c.id) && (drafting.has(c.id) || (draftsComing && c.status === 'queued'))).length;
  const toggle = (id: string, on: boolean) =>
    setPicked((cur) => {
      const next = new Set(cur === 'all' ? ids : cur);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const selectAll = (on: boolean) => setPicked(on ? 'all' : new Set());
  const boxFor = (c: OutreachCandidate) =>
    ids.has(c.id) ? <SelectBox name={c.name} selected={selected.has(c.id)} onSelect={(on) => toggle(c.id, on)} /> : null;
  return { approvable, selected, allOn, selectAll, stillDrafting, boxFor };
}

export type BulkSelection = ReturnType<typeof useBulkSelection>;
